'use client';

import clsx from 'clsx';
import { type ForwardedRef, useImperativeHandle, useRef } from 'react';
import {
  Input as RACInput,
  SearchField as RACSearchField,
  type SearchFieldProps as RACSearchFieldProps,
  Group as RACGroup,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/localization-provider.js';
import { IconSearch } from '../../icons/index.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { FormFieldDescription, FormFieldError, FormFieldLabel } from '../internal/form-field/form-field.js';
import { XButton } from '../internal/x-button/x-button.js';
import type { ApiProps, CommonProps, FieldWithPlaceholderProps } from '../types.js';
import { textStyle } from '../typography/utils.js';

export interface SearchFieldProps
  extends
    CommonProps,
    FieldWithPlaceholderProps<string>,
    ApiProps<SearchFieldApi>,
    Pick<
      RACSearchFieldProps,
      | 'value'
      | 'defaultValue'
      | 'onChange'
      | 'onSubmit'
      | 'onClear'
      | 'isDisabled'
      | 'isRequired'
      | 'isInvalid'
      | 'isReadOnly'
      | 'autoFocus'
      | 'autoComplete'
      | 'autoCorrect'
      | 'spellCheck'
      | 'inputMode'
      | 'minLength'
      | 'maxLength'
      | 'pattern'
      | 'onFocus'
      | 'onBlur'
    > {}

/** The imperative API exposed by the `SearchField` component. */
export interface SearchFieldApi {
  /** Focuses the input field. */
  focus: () => void;
  /** Selects the contents of the input field. */
  select: () => void;
}

function SearchField(
  {
    label,
    description,
    error: errorMessage,
    placeholder,
    UNSAFE_className,
    UNSAFE_style,
    apiRef,
    ...props
  }: SearchFieldProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const inputRef = useRef<HTMLInputElement>(null);

  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('SearchField requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  const commonMessages = useLocalizedMessages('common');

  useImperativeHandle(
    apiRef,
    () => ({
      focus: () => inputRef.current?.focus(),
      select: () => inputRef.current?.select(),
    }),
    [],
  );

  return (
    <RACSearchField
      {...props}
      ref={ref}
      className={clsx('cim-search-field', UNSAFE_className)}
      style={UNSAFE_style}
      enterKeyHint="search"
    >
      {({ state }) => (
        <>
          <FormFieldLabel isRequired={props.isRequired} isDisabled={props.isDisabled}>
            {label}
          </FormFieldLabel>

          <RACGroup className="cim-search-field-input-group" onClick={() => inputRef.current?.focus()}>
            <div
              className={clsx(
                'cim-text-field-affix',
                textStyle({ variant: 'body', tone: props.isDisabled ? 'muted' : 'subtle' }),
              )}
              aria-hidden
            >
              <IconSearch />
            </div>

            <RACInput
              ref={inputRef}
              className={clsx('cim-search-field-input', textStyle({ variant: 'body' }))}
              placeholder={placeholder}
            />

            <XButton
              UNSAFE_className="cim-search-field-clear"
              data-visible={(state.value !== '' && !props.isDisabled && !props.isReadOnly) || undefined}
              aria-label={commonMessages.format('clearValue')}
              iconType="circled"
            />
          </RACGroup>

          <FormFieldError>{errorMessage}</FormFieldError>
          <FormFieldDescription>{description}</FormFieldDescription>
        </>
      )}
    </RACSearchField>
  );
}

/**
 * Allows users to enter and clear a search query.
 *
 * See [search field usage guidelines](https://ui.cimpress.io/components/search-field/).
 */
const _SearchField = withStyleProps(forwardRef(SearchField), 'SearchField');

export { _SearchField as SearchField };
